/**
 * הלוואות — REDESIGN §6, a first-class screen (new).
 *
 *   header figures  סך החוב (the screen's one hero) · תשלום חודשי · ריבית משוקללת · הריבית שתשלם השנה
 *   trend           total debt over the last 12 months + the live point, and the monthly change in words
 *   `הכי יקר קודם`   the interest ranking — the DEFAULT sort — with the ריבית · יתרה · תשלום · סיום switch
 *   timeline        when each loan ends, over the next 5 years, plus §6's "what frees up when" sentence
 *   scope           `הכל · פרטי · עסקי`, driven by the account-assignment table
 *
 * Bank loans and issuer facilities never mix: `kind: 'card-credit'` rows are grouped under their
 * card and labelled `קרדיט בכרטיס` (BRIEF §9.4). A row expands to the interest structure the API
 * delivers (fixed vs index-linked, tiers), the contract dates and a link to its transactions.
 *
 * Every figure comes from `GET /api/loans` (`LoansResponse`) — totals, the weighted rate, the
 * annual-interest estimate, the trend and `endingSoon` are all server-computed. This screen adds
 * only the scope filter, the strip positions and the axis step (`web/src/loans.ts`, unit-tested).
 */

import { useMemo, useState } from 'react';
import type { LoanInterestTier, LoanRow, LoanSort, LoansResponse, LoansTotals, SummaryResponse } from '../../../shared-types.ts';
import { fmtDate, fmtMoney, fmtNum, monthName, monthShort } from '../format.ts';
import { ACCOUNT_TYPE, he, plural, providerName } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Chip, EmptyState, Ltr, Money, ProviderLogo, ScreenSkeleton, Section, Segmented } from '../components/ui.tsx';
import { Figure } from '../components/insight.tsx';
import { LineChart } from '../charts/LineChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import {
  DEFAULT_LOAN_SORT,
  LOAN_SCOPES,
  LOAN_SORT_ORDER,
  RANK_GROUP_LIMIT,
  endTimeline,
  filterByScope,
  isLoanScope,
  isLoanSort,
  rankGroups,
  scopeTotals,
  topRateRow,
  trendStep,
} from '../loans.ts';
import type { LoanRankGroup, LoanScope, LoanSubGroup } from '../loans.ts';

const SCOPE_LABEL: Readonly<Record<LoanScope, string>> = {
  all: he.loans.scopeAll,
  personal: he.loans.scopePersonal,
  business: he.loans.scopeBusiness,
};

const SORT_LABEL: Readonly<Record<LoanSort, string>> = {
  rate: he.loans.sortRate,
  balance: he.loans.sortBalance,
  payment: he.loans.sortMonthly,
  end: he.loans.sortEnd,
};

export function LoansScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api } = useApp();
  const { route, navigate } = useRouter();

  const scope: LoanScope = isLoanScope(route.query.scope) ? route.query.scope : 'all';
  const sort: LoanSort = isLoanSort(route.query.sort) ? route.query.sort : DEFAULT_LOAN_SORT;

  // The sort is a server query (`?sort=`), so each order is its own cache entry.
  const loans = useQuery<LoansResponse>(cacheKey('loans', { sort }), () => api.loans({ sort }), {
    scopes: ['accounts', 'networth', 'summary'],
  });

  const setQuery = (next: { scope?: LoanScope; sort?: LoanSort }) =>
    navigate(buildPath('loans', null, { scope: next.scope ?? scope, sort: next.sort ?? sort }), { replace: true });

  const data = loans.data;
  const rows = useMemo(() => filterByScope(data?.items ?? [], scope), [data, scope]);
  const closed = useMemo(() => filterByScope(data?.closed ?? [], scope), [data, scope]);

  if (!data) {
    if (loans.error) {
      return (
        <div className="screen">
          <EmptyState illustration="reconnect" title={he.states.loadFailed} body={he.states.tryAgain} />
        </div>
      );
    }
    return <ScreenSkeleton />;
  }

  if (!data.items.length && !data.closed.length) {
    return (
      <div className="screen">
        <EmptyState illustration="noResults" title={he.loans.empty} body={he.loans.emptyBody} />
      </div>
    );
  }

  const totals = scopeTotals(data, scope);
  const asOf = summary?.asOf ?? data.asOf;

  return (
    <div className="screen loans">
      <LoansHeader totals={totals} scope={scope} onScope={(next) => setQuery({ scope: next })} />

      {rows.length === 0 ? (
        <EmptyState illustration="noResults" title={he.loans.emptyScope} body={he.loans.emptyScopeBody} />
      ) : (
        <>
          <TrendSection response={data} scope={scope} />
          <RankSection rows={rows} sort={sort} onSort={(next) => setQuery({ sort: next })} />
          <TimelineSection response={data} rows={rows} asOf={asOf} scope={scope} />
        </>
      )}

      {closed.length ? <ClosedSection rows={closed} /> : null}
    </div>
  );
}

// ───────────────────────────── header (§6 header figures) ─────────────────────────────

function LoansHeader({
  totals,
  scope,
  onScope,
}: {
  totals: LoansTotals;
  scope: LoanScope;
  onScope: (next: LoanScope) => void;
}) {
  const estimated = totals.monthlyPaymentEstimatedAgorot;
  return (
    <section className="loans-head">
      <div className="row between wrap" style={{ alignItems: 'flex-start' }}>
        <div>
          <span className="fk-meta">{he.loans.debtTotal}</span>
          <p className="fk-hero">
            <Money agorot={totals.debtAgorot} />
          </p>
          <p className="fk-hero-inputs">{totals.openCount === 1 ? he.loans.debtOfOne : he.loans.debtOf(totals.openCount)}</p>
        </div>
        <Segmented
          value={scope}
          options={LOAN_SCOPES.map((key) => [key, SCOPE_LABEL[key]] as const)}
          onChange={onScope}
          label={he.loans.scope}
        />
      </div>
      <div className="loans-figs">
        <Figure
          label={he.loans.monthly}
          value={fmtMoney(totals.monthlyPaymentAgorot)}
          note={estimated > 0 ? he.loans.monthlyEstimatedOf(fmtMoney(estimated)) : he.loans.monthlyAllKnown}
        />
        <Figure
          label={he.loans.weightedRate}
          value={totals.weightedRatePct === null ? he.app.unavailable : he.loans.ratePct(fmtNum(totals.weightedRatePct, 2))}
          note={he.loans.weightedRateNote}
        />
        <Figure
          label={he.loans.yearInterest}
          value={`≈ ${fmtMoney(totals.estimatedAnnualInterestAgorot)}`}
          note={he.loans.yearInterestNote}
        />
      </div>
    </section>
  );
}

// ───────────────────────────── the debt trend (§6) ─────────────────────────────

function TrendSection({ response, scope }: { response: LoansResponse; scope: LoanScope }) {
  const { trend } = response;
  if (trend.points.length < 2) return null;
  const change = trend.avgMonthlyChangeAgorot ?? trend.changeAgorot;
  const sentence =
    change === 0
      ? he.loans.trendFlat
      : change < 0
        ? he.loans.trendDown(fmtMoney(Math.abs(change)))
        : he.loans.trendUp(fmtMoney(change));
  const first = trend.points[0]!;
  const last = trend.points[trend.points.length - 1]!;
  const step = trendStep(trend.points.map((point) => point.debtAgorot));
  return (
    <Section span={12} className="loans-trend" ariaLabel={he.loans.trendQ}>
      <LineChart
        question={he.loans.trendQ}
        period={he.loans.trendPeriod(monthName(first.month), monthName(last.month))}
        summary={sentence}
        step={step}
        points={trend.points.map((point) => ({
          label: monthShort(point.month),
          value: point.debtAgorot,
          aria: `${monthShort(point.month)} ${fmtMoney(point.debtAgorot)}${point.estimated ? ` (${he.nw.estimated})` : ''}`,
          tip: {
            title: monthShort(point.month),
            rows: [{ value: fmtMoney(point.debtAgorot), label: point.estimated ? he.nw.estimated : he.loans.trendTblDebt }],
          },
        }))}
        table={{
          head: [he.loans.trendTblMonth, he.loans.trendTblDebt],
          rows: trend.points.map((point) => [
            monthShort(point.month),
            `${fmtMoney(point.debtAgorot)}${point.estimated ? ` (${he.nw.estimated})` : ''}`,
          ]),
        }}
      />
      <p className="fk-body">{sentence}</p>
      {scope === 'all' ? null : <p className="fk-meta">{he.loans.trendAllScopes}</p>}
    </Section>
  );
}

// ───────────────────────────── the ranking (§6) ─────────────────────────────

function RankSection({
  rows,
  sort,
  onSort,
}: {
  rows: readonly LoanRow[];
  sort: LoanSort;
  onSort: (next: LoanSort) => void;
}) {
  const groups = useMemo(() => rankGroups(rows, sort), [rows, sort]);
  const top = useMemo(() => topRateRow(rows), [rows]);
  return (
    <Section
      span={12}
      className="loans-rank"
      titleId="h-loans-rank"
      title={he.loans.rankTitle}
      sub={sort === 'rate' ? he.loans.rankSub : he.loans.rankSubOther}
      action={
        <Segmented
          value={sort}
          options={LOAN_SORT_ORDER.map((key) => [key, SORT_LABEL[key]] as const)}
          onChange={onSort}
          label={he.loans.sortLabel}
        />
      }
    >
      {/* §6 forbids mixing card facilities into the bank list, so no single list can be a global
          interest ranking. This line answers "מה הכי יקר" outright, whatever the grouping does. */}
      {top === null ? null : (
        <p className="loans-top">
          <Chip tone="warn">{he.loans.topRate}</Chip>{' '}
          <span>
            {he.loans.topRateOf(
              top.name,
              top.ratePct === null ? he.app.unavailable : he.loans.ratePct(fmtNum(top.ratePct, 2)),
            )}
          </span>{' '}
          <span className="fk-meta">
            <span className="num tab">{fmtMoney(top.balanceAgorot)}</span>
            {top.monthlyPaymentAgorot === null ? null : (
              <>
                {' · '}
                <span className="num tab">{fmtMoney(top.monthlyPaymentAgorot)}</span> {he.loans.monthlyShort}
              </>
            )}
          </span>
        </p>
      )}
      {groups.map((group, index) => (
        <LoanGroup
          key={group.kind}
          title={group.kind === 'bank' ? he.loans.bankGroup : he.loans.cardGroup}
          group={group}
          leading={index === 0}
        />
      ))}
    </Section>
  );
}

function LoanGroup({ title, group, leading }: { title: string; group: LoanRankGroup; leading: boolean }) {
  return (
    <div className="loangroup">
      <h3 className="fk-meta">{title}</h3>
      {group.subs.map((sub, index) => (
        <LoanSub
          key={sub.key}
          sub={sub}
          standalone={group.subs.length === 1 && sub.card === null}
          // The top-ranked card of the top-ranked group opens on load, so `הכי יקר קודם` still
          // SHOWS the most expensive rows; the rest stay collapsed so 12 facilities are not a wall.
          defaultOpen={leading && index === 0}
        />
      ))}
    </div>
  );
}

/**
 * One card's facilities behind a collapsible header (`Cal ***8993 · 10 מסגרות · יתרה ₪X · ₪Y לחודש`),
 * or — on the bank group, which has a single implicit sub-group — the rows straight up. Either way
 * the list stops at `RANK_GROUP_LIMIT` rows with `עוד N`, so 19 open loans never render as one wall.
 */
function LoanSub({ sub, standalone, defaultOpen }: { sub: LoanSubGroup; standalone: boolean; defaultOpen: boolean }) {
  const [open, setOpen] = useState(standalone || defaultOpen);
  const panelId = `loansub-${sub.key}`;
  const list = <LoanRowList rows={sub.rows} />;
  if (standalone) return list;
  const cardName = sub.card ? `${sub.card.name}${sub.card.numberLast4 ? ` ***${sub.card.numberLast4}` : ''}` : sub.rows[0]?.name ?? '';
  return (
    <div className="loansub">
      <button type="button" className="loansub-head" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
        <ProviderLogo providerId={sub.card?.providerId ?? sub.rows[0]!.providerId} title={providerName(sub.rows[0]!.providerId)} />
        <span className="main">
          <span className="name">
            <Ltr>{cardName}</Ltr>
          </span>
          <span className="sub">
            {sub.count === 1 ? he.loans.cardFacilityOne : he.loans.cardFacilities(sub.count)}
            {' · '}
            {he.loans.groupBalance} <span className="num tab">{fmtMoney(sub.balanceAgorot)}</span>
            {sub.monthlyPaymentAgorot > 0 ? (
              <>
                {' · '}
                <span className="num tab">{fmtMoney(sub.monthlyPaymentAgorot)}</span> {he.loans.monthlyShort}
              </>
            ) : null}
          </span>
        </span>
        <Icon name={open ? 'chevU' : 'chevD'} className="xs t3" />
      </button>
      {open ? (
        <div id={panelId}>{list}</div>
      ) : null}
    </div>
  );
}

function LoanRowList({ rows }: { rows: readonly LoanRow[] }) {
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, RANK_GROUP_LIMIT);
  const hidden = rows.length - shown.length;
  return (
    <>
      <div className="hairlist">
        {shown.map((row) => (
          <LoanRowView key={row.accountId} row={row} />
        ))}
      </div>
      {hidden > 0 || open ? (
        <button type="button" className="link moretoggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? he.loans.less : plural(hidden, he.loans.moreOne, he.loans.moreMany(hidden))}
          <Icon name={open ? 'chevU' : 'chevD'} className="xs" />
        </button>
      ) : null}
    </>
  );
}

/** One ranked row; it expands in place to the interest structure the API delivers (§6). */
function LoanRowView({ row }: { row: LoanRow }) {
  const [open, setOpen] = useState(false);
  const panelId = `loan-${row.accountId}`;
  return (
    <div className="loanrow">
      <button
        type="button"
        className="loanrow-head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <ProviderLogo providerId={row.providerId} title={providerName(row.providerId)} />
        <span className="main">
          <span className="name">
            {row.name}
            {row.numberLast4 ? (
              <>
                {' '}
                <Ltr>{`***${row.numberLast4}`}</Ltr>
              </>
            ) : null}
          </span>
          <span className="sub">
            {row.kind === 'card-credit'
              ? he.loans.cardCreditOf(row.card ? `${row.card.name}${row.card.numberLast4 ? ` ***${row.card.numberLast4}` : ''}` : providerName(row.providerId))
              : (row.product ?? ACCOUNT_TYPE.LOAN)}
            {' · '}
            {row.endDate ? he.loans.ends(fmtDate(row.endDate)) : he.loans.noEnd}
            {row.assignment === 'business' ? (
              <>
                {' '}
                <Chip tone="muted">{he.loans.business}</Chip>
              </>
            ) : null}
          </span>
        </span>
        <span className="loanrate">
          <span className="num tab">{row.ratePct === null ? he.app.unavailable : he.loans.ratePct(fmtNum(row.ratePct, 2))}</span>
          <span className="fk-meta">
            {row.estimatedAnnualInterestAgorot === null ? he.loans.noRate : he.loans.yearly(fmtMoney(row.estimatedAnnualInterestAgorot))}
          </span>
        </span>
        <span className="loanfigs">
          <span className="num tab">{fmtMoney(row.balanceAgorot)}</span>
          <span className="fk-meta">
            {row.monthlyPaymentAgorot === null ? (
              he.app.unavailable
            ) : (
              <>
                <span className="num">{fmtMoney(row.monthlyPaymentAgorot)}</span> {he.loans.monthlyShort}
                {row.monthlyPaymentEstimated ? ` · ${he.loans.estimated}` : ''}
              </>
            )}
          </span>
        </span>
        <Icon name={open ? 'chevU' : 'chevD'} className="xs t3" />
      </button>
      {open ? <LoanDetails row={row} panelId={panelId} /> : null}
    </div>
  );
}

function structureLabel(row: LoanRow): string {
  const { interest } = row;
  if (interest.structure === 'prime') {
    return interest.indexApplied
      ? he.loans.ratePrimePlus(interest.indexName ?? he.loans.indexDefault, fmtNum(interest.fixedPct ?? 0, 2))
      : he.loans.rateIndexed(interest.indexName ?? he.loans.indexDefault);
  }
  if (interest.structure === 'fixed') return he.loans.rateFixed;
  return he.loans.rateUnknownType;
}

function LoanDetails({ row, panelId }: { row: LoanRow; panelId: string }) {
  const tiers = row.interest.tiers;
  return (
    <div className="loandetails" id={panelId}>
      <dl className="kv">
        <dt>{he.loans.rateStructure}</dt>
        <dd>
          {structureLabel(row)}
          {row.ratePct === null ? '' : ` · ${he.loans.ratePct(fmtNum(row.ratePct, 2))}`}
        </dd>
        <dt>{he.loans.contract}</dt>
        <dd>
          {row.startDate ? he.loans.contractFrom(fmtDate(row.startDate)) : ''}
          {row.startDate && row.endDate ? ' · ' : ''}
          {row.endDate ? he.loans.ends(fmtDate(row.endDate)) : he.loans.noEnd}
          {row.endsInMonths === null ? '' : ` · ${he.loans.endsInMonths(row.endsInMonths)}`}
        </dd>
        <dt>{he.loans.monthly}</dt>
        <dd>
          {row.monthlyPaymentAgorot === null
            ? he.app.unavailable
            : `${fmtMoney(row.monthlyPaymentAgorot)} · ${he.loans.paymentSource[row.monthlyPaymentSource]}`}
        </dd>
        {row.note?.remainingPayments == null ? null : (
          <>
            <dt>{he.loans.remainingLabel}</dt>
            <dd>{he.loans.remaining(row.note.remainingPayments)}</dd>
          </>
        )}
      </dl>
      {tiers.length > 1 ? <TierTable tiers={tiers} /> : null}
      <Link className="link" to={buildPath('transactions', null, { account: row.accountId })}>
        {he.loans.toTransactions}
        <Icon name="chevL" className="xs" />
      </Link>
    </div>
  );
}

/** The provider's `interest[]` rows, when there is more than one (tiers / fixed + index). */
function TierTable({ tiers }: { tiers: readonly LoanInterestTier[] }) {
  return (
    <table className="utable tiers">
      <thead>
        <tr>
          <th>{he.loans.tierKind}</th>
          <th>{he.loans.tierRate}</th>
          <th>{he.loans.tierRange}</th>
        </tr>
      </thead>
      <tbody>
        {tiers.map((tier, index) => (
          <tr key={`${tier.kind ?? 'x'}-${index}`} className={tier.applicable ? 'on' : undefined}>
            <td>{tier.indexed ? he.loans.rateIndexed(tier.indexName ?? he.loans.indexDefault) : he.loans.rateFixed}</td>
            <td className="num tab">{tier.ratePct === null ? he.app.unavailable : he.loans.ratePct(fmtNum(tier.ratePct, 2))}</td>
            <td className="num">
              {tier.fromAgorot === null && tier.toAgorot === null
                ? he.loans.tierAll
                : `${fmtMoney(tier.fromAgorot ?? 0)}–${tier.toAgorot === null ? '∞' : fmtMoney(tier.toAgorot)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ───────────────────────────── the end-date timeline (§6) ─────────────────────────────

function TimelineSection({
  response,
  rows,
  asOf,
  scope,
}: {
  response: LoansResponse;
  rows: readonly LoanRow[];
  asOf: string;
  scope: LoanScope;
}) {
  const timeline = endTimeline(rows, asOf);
  const ending = response.endingSoon;
  const sentence =
    ending.count === 0 || ending.withinMonths === null
      ? he.loans.timelineNone
      : ending.monthlyFreedAgorot > 0
        ? ending.count === 1
          ? he.loans.timelineFreeOne(ending.withinMonths, fmtMoney(ending.monthlyFreedAgorot))
          : he.loans.timelineFree(ending.withinMonths, ending.count, fmtMoney(ending.monthlyFreedAgorot))
        : ending.count === 1
          ? he.loans.timelineFreeNoAmountOne(ending.withinMonths)
          : he.loans.timelineFreeNoAmount(ending.withinMonths, ending.count);
  return (
    <Section span={12} className="loans-timeline" titleId="h-loans-tl" title={he.loans.timelineTitle} sub={he.loans.timelineQ}>
      {/* Time runs LTR even on an RTL page (spec §6/§8.5). The strip itself is decorative — the
          same markers are a real list for assistive technology directly below it. */}
      <div className="tlstrip" dir="ltr" aria-hidden="true">
        <div className="tltrack">
          {timeline.marks.map((mark) => (
            <span
              key={mark.accountId}
              className="tlmark"
              style={{ insetInlineStart: `${(mark.position * 100).toFixed(2)}%` }}
              title={`${mark.name} · ${fmtDate(mark.endDate)}`}
            />
          ))}
        </div>
        <div className="tlyears" aria-hidden="true">
          {timeline.years.map((year) => (
            <span key={year} className="num">
              {year}
            </span>
          ))}
        </div>
      </div>
      <ul className="sr-only">
        {timeline.marks.map((mark) => (
          <li key={mark.accountId}>{`${mark.name} · ${fmtDate(mark.endDate)}`}</li>
        ))}
      </ul>
      <p className="fk-body">{scope === 'all' ? sentence : he.loans.timelineScoped}</p>
      {timeline.beyond > 0 || timeline.undated > 0 ? (
        <p className="fk-meta">
          {timeline.beyond > 0 ? he.loans.timelineBeyond(timeline.beyond, timeline.toYear) : ''}
          {timeline.beyond > 0 && timeline.undated > 0 ? ' · ' : ''}
          {timeline.undated > 0 ? he.loans.timelineUndated(timeline.undated) : ''}
        </p>
      ) : null}
    </Section>
  );
}

// ───────────────────────────── loans past their end date ─────────────────────────────

function ClosedSection({ rows }: { rows: readonly LoanRow[] }) {
  const total = rows.reduce((sum, row) => sum + row.balanceAgorot, 0);
  return (
    <details className="acc loans-closed">
      <summary>
        <span>{he.nw.closedLoans}</span>
        <Chip tone="muted">{he.loans.closedCount(rows.length)}</Chip>
        <Icon name="chevD" />
      </summary>
      <p className="fk-meta">{he.loans.closedNote}</p>
      <div className="hairlist">
        {rows.map((row) => (
          <div className="listrow" key={row.accountId}>
            <ProviderLogo providerId={row.providerId} size="xs" title={providerName(row.providerId)} />
            <span className="main">
              <span className="name">{row.name}</span>
              <span className="sub">{row.endDate ? he.loans.ends(fmtDate(row.endDate)) : he.loans.noEnd}</span>
            </span>
            <span className="amt">
              <span className="num tab">{fmtMoney(row.balanceAgorot)}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="fk-meta">
        {he.loans.debtTotal} <span className="num">{fmtMoney(total)}</span>
      </p>
    </details>
  );
}
